/**
 * Checks if conditions are met based on blockState values
 * @param {Array} conditions - Array of condition objects
 * @param {Object} blockState - The state object containing field values
 * @returns {boolean} - Returns true if conditions are met, false otherwise
 */
export const checkConditions = (conditions, blockState) => {
    if (!conditions || conditions.length === 0) return true;

    let result = null;

    for (const cond of conditions) {
        const fieldValue = blockState[cond.selectedConditionDataElement];
        if (fieldValue === undefined || fieldValue === null) continue;

        let isMet = false;
        switch (cond.selectedConditionOperator) {
            case 'is_equals':
                isMet = fieldValue === cond.value;
                break;
            case 'is_not_equals':
                isMet = fieldValue !== cond.value;
                break;
            case 'contains':
                isMet = Array.isArray(fieldValue) && fieldValue.includes(cond.value);
                break;
            case 'not_contains':
                isMet = Array.isArray(fieldValue) && !fieldValue.includes(cond.value);
                break;
            default:
                isMet = true;
        }

        if (cond.selectedConditionClause === 'or') {
            result = result === null ? isMet : result || isMet;
        } else if (cond.selectedConditionClause === 'and') {
            result = result === null ? isMet : result && isMet;
        }
    }
    return result === null ? false : result;
};


export const generateId = (sentence) => {
    const prefix = sentence
        .split(" ")
        .map(word => word.charAt(0).toUpperCase())
        .join("");
    const randomNum = Math.floor(100000000 + Math.random() * 900000000);
    return `#${prefix}${randomNum}`;
};

export const clearCurrentChartReduxStore = (props, subKeys) => {
    props.clearReduxDataOfCurrentComponent({
        key: 'document',
        subKey: subKeys
    })
}

export const chatRedirect = (history, appid, listPages) => {
    history?.push({
        pathname: `/${appid}/page/${listPages}`
    });
}

export { toDateOnlyString, normalizeDatesInObject } from './dateNormalization';

/**
 * Format a date in PST (America/Los_Angeles).
 * @param {Date|string|number} date
 * @returns {string} e.g. "1/30/2026, 3:45:00 PM PST"
 */
export const formatDateInPST = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', timeZoneName: 'short' });
};

const DEAL_REVIEW_HISTORY_PST_FORMAT = /^\d{2}\s-\s[A-Za-z]{3}\s-\s\d{4}\s+\d{2}:\d{2}:\d{2}\sPST$/;
const CLOCK_12H_FORMAT = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i;

/**
 * Formats reset history timestamp in PST: DD - MMM - YYYY HH:MM:SS PST
 * @param {Date|string|number} date
 * @returns {string} e.g. "16 - Jul - 2026 03:42:42 PST"
 */
export const formatDealReviewHistoryTimePST = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';

    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(d);

    const part = (type) => parts.find((p) => p.type === type)?.value ?? '';
    let hour = part('hour');
    if (hour === '24') hour = '00';

    return `${part('day')} - ${part('month')} - ${part('year')} ${hour}:${part('minute')}:${part('second')} PST`;
};

/** Bare clock time with no date, e.g. "08:21 AM" (what the reset-history API currently sends). */
const BARE_CLOCK_TIME_FORMAT = /^\d{1,2}:\d{2}\s?(AM|PM)$/i;

/**
 * Normalizes reset history time for display (passes through PST format; legacy values unchanged).
 * The reset-history API only sends a bare "hh:mm A" clock time with no date, so it can't be
 * converted into the full "DD - MMM - YYYY   HH:MM:SS PST" shape on the frontend alone — it is
 * labeled PST as-is instead.
 * @param {string} timeValue
 * @returns {string}
 */
export const formatDealReviewResetDisplayTime = (timeValue) => {
    if (timeValue == null || timeValue === '') return '';
    const trimmed = String(timeValue).trim();
    if (DEAL_REVIEW_HISTORY_PST_FORMAT.test(trimmed)) return trimmed;
    if (BARE_CLOCK_TIME_FORMAT.test(trimmed)) return `${trimmed} PST`;
    const formatted = formatDealReviewHistoryTimePST(timeValue);
    return formatted || trimmed;
};

/**
 * Formats decision-history dialog timestamp from entry.updatedAtPST / entry.updatedAt.
 * @param {{ updatedAtPST?: string, updatedAt?: string|Date }} entry
 * @returns {string}
 */
export const formatDecisionHistoryDisplayTime = (entry) => {
    if (!entry || typeof entry !== 'object') return '';

    const iso = entry.updatedAt;
    if (iso) {
        const fromIso = formatDealReviewHistoryTimePST(iso);
        if (fromIso) return fromIso;
    }

    const pstField = entry.updatedAtPST;
    if (pstField != null && pstField !== '') {
        const trimmed = String(pstField).trim();
        if (DEAL_REVIEW_HISTORY_PST_FORMAT.test(trimmed)) return trimmed;
        const parsed = new Date(pstField);
        if (!Number.isNaN(parsed.getTime())) {
            return formatDealReviewHistoryTimePST(parsed);
        }
        return trimmed;
    }

    return '';
};

/**
 * Get value from object by dot path (e.g. "address.city")
 */
export const getValueFromPath = (obj, path) => {
    if (!path) return undefined;
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
};

/**
 * Returns validation errors for required fields across all blocks.
 * Uses same logic as SummaryBlock: merged data elements, conditions, and isEmpty check.
 * @returns {{ isValid: boolean, errors: Array<{ label: string }> }}
 */
export const getRequiredFieldsValidationErrors = (blocks, blockState, formData, selectedDataModel) => {
    const errors = [];
    if (!blocks?.length || !blockState) return { isValid: true, errors };

    const dataModelElements = formData?.dataModel?.[selectedDataModel]?.dataElements || [];

    const collectRequiredFromBlock = (block) => {
        const dataElements = block?.config?.dataElements;
        if (!dataElements?.length) return;
        const fdes = Array.isArray(dataElements) ? dataElements : [dataElements];
        fdes.forEach((fde) => {
            const sde = dataModelElements.find((de) => de.name === fde.value);
            const element = sde?.name ? { ...sde, ...fde } : { ...fde };
            if (!element.isRequired) return;
            if (!checkConditions(element.conditions, blockState)) return;
            const rawValue = getValueFromPath(blockState, element.value) ?? blockState[element.name];
            const isEmpty = rawValue === undefined || rawValue === null || rawValue === '' || (Array.isArray(rawValue) && rawValue.length === 0);
            const topTierVal = blockState.topTierRequired ?? blockState.topTierRequired;
            const maxTierMatch = topTierVal != null && String(topTierVal).match(/tier\s*(\d+)/i);
            const maxTier = maxTierMatch ? parseInt(maxTierMatch[1], 10) : 2; // default: all required

            const tierMatch = element.value && String(element.value).match(/tier(\d+)/i);
            const elementTier = element.tierLevel ?? (tierMatch ? parseInt(tierMatch[1], 10) : null);

            // Skip required check for tier fields above maxTier
            if (elementTier != null && elementTier > maxTier) return;

            if (isEmpty) {
                errors.push({ label: element.label || 'This field' });
            }
        });
    };

    blocks.forEach((block) => {
        if (block.subBlock?.length) {
            block.subBlock.forEach((res) => collectRequiredFromBlock(res));
        } else {
            collectRequiredFromBlock(block);
        }
    });

    return { isValid: errors.length === 0, errors };
};

/**
 * Default tier date validation rules (used when block.config.tierDateValidation is not provided).
 * Each rule: { beforeField, afterField, message }
 */
/**
 * Default initial field values when creating a new deal (used when block.config.initialDefaults is not provided).
 * Add to block.config.initialDefaults to override per app/chart.
 */
export const DEFAULT_INITIAL_DEFAULTS = {
    dealIdPrefix: 'Deal Review',
    dealValue: 0,
    needToKnowSession: 'FALSE',
    priority: 'Normal',
    negotiationStatus: 'Deal Review',
};

/**
 * Collects initial defaults from blocks (block.config.initialDefaults).
 * Merges with DEFAULT_INITIAL_DEFAULTS; block config overrides defaults.
 */
export const getInitialDefaultsFromBlocks = (blocks) => {
    let merged = { ...DEFAULT_INITIAL_DEFAULTS };
    const collect = (block) => {
        const cfg = block?.config?.initialDefaults;
        if (cfg && typeof cfg === 'object') {
            merged = { ...merged, ...cfg };
        }
    };
    if (blocks?.length) {
        blocks.forEach((b) => {
            if (b.subBlock?.length) b.subBlock.forEach(collect);
            else collect(b);
        });
    }
    return merged;
};

/**
 * Default status transition rules (used when block.config.statusTransition is not provided).
 * When a trigger field gets a value: set statusField to toStatus.
 * When a trigger field is cleared: set statusField to whenValueCleared (or delete if not specified).
 *
 * Config structure:
 * {
 *   statusField: string,  // e.g. 'negotiationStatus'
 *   transitions: [
 *     { triggerFields: string[], toStatus: string, whenValueCleared?: string },
 *     ...
 *   ]
 * }
 */
export const DEFAULT_STATUS_TRANSITION = {
    statusField: 'negotiationStatus',
    transitions: [
        {
            triggerFields: ['topTierRequired'],
            whenValueCleared: 'Deal Review',
            onlyWhenCleared: true,
        },
        {
            triggerFields: ['tier0ApprovalDate', 'tier1ApprovalDate', 'tier2ApprovalDate'],
            toStatus: 'Negotiation in progress',
            whenValueCleared: 'Deal Review',
        },
        {
            triggerFields: ['approvalDate', 'negotiationCompletionDate'],
            toStatus: 'Negotiation completed',
            whenValueCleared: 'Negotiation in progress',
        },
    ],
};

/**
 * Collects all statusTransition configs from blocks (including manualStatusValidation-only configs).
 */
const collectStatusTransitionConfigs = (blocks) => {
    const configs = [];
    const collect = (block) => {
        const cfg = block?.config?.statusTransition;
        if (!cfg || typeof cfg !== 'object') return;
        const hasTransitions = typeof cfg.statusField === 'string'
            && Array.isArray(cfg.transitions)
            && cfg.transitions.length > 0;
        const hasManualValidation = Array.isArray(cfg.manualStatusValidation)
            && cfg.manualStatusValidation.length > 0;
        if (hasTransitions || hasManualValidation) {
            configs.push(cfg);
        }
    };
    if (blocks?.length) {
        blocks.forEach((b) => {
            if (b.subBlock?.length) b.subBlock.forEach(collect);
            else collect(b);
        });
    }
    return configs;
};

/**
 * Resolves status transition config from blocks, merging manualStatusValidation from all blocks.
 * Falls back to DEFAULT_STATUS_TRANSITION when no config.
 */
const getStatusTransitionFromBlocks = (blocks) => {
    const configs = collectStatusTransitionConfigs(blocks);
    if (!configs.length) {
        return DEFAULT_STATUS_TRANSITION;
    }

    const manualStatusValidation = configs.flatMap((cfg) => (
        Array.isArray(cfg.manualStatusValidation) ? cfg.manualStatusValidation : []
    ));

    const baseConfig = configs.find((cfg) => (
        Array.isArray(cfg.manualStatusValidation) && cfg.manualStatusValidation.length > 0
    ))
        || configs.find((cfg) => (
            typeof cfg.statusField === 'string'
            && Array.isArray(cfg.transitions)
            && cfg.transitions.length > 0
        ))
        || configs[configs.length - 1];

    if (manualStatusValidation.length > 0) {
        return { ...baseConfig, manualStatusValidation };
    }
    return baseConfig;
};

const normalizeManualValidationFieldValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
        const innerValue = value.value ?? value.name ?? value.label;
        return innerValue === null || innerValue === undefined ? '' : String(innerValue).trim();
    }
    return String(value).trim();
};

const manualValidationStatusesMatch = (left, right) => (
    normalizeManualValidationFieldValue(left).toLowerCase()
    === normalizeManualValidationFieldValue(right).toLowerCase()
);

const getManualValidationFieldValue = (blockState, fieldKey) => {
    if (!fieldKey || !blockState) {
        return undefined;
    }
    if (fieldKey.includes('.')) {
        return getValueFromPath(blockState, fieldKey);
    }
    return blockState[fieldKey];
};

const hasManualValidationFieldValue = (value) => {
    const normalized = normalizeManualValidationFieldValue(value);
    if (normalized === '') {
        return false;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(normalized) || /^\d{2}\/\d{2}\/\d{4}/.test(normalized)) {
        return !Number.isNaN(Date.parse(normalized));
    }
    return true;
};

const getManualStatusValidationErrorForRule = (rule, statusValue, blockState) => {
    if (!rule?.status || !manualValidationStatusesMatch(rule.status, statusValue)) {
        return null;
    }

    const requireOf = Array.isArray(rule.requireOf) ? rule.requireOf : [];
    const requireAnyOf = Array.isArray(rule.requireAnyOf) ? rule.requireAnyOf : [];

    if (requireOf.length > 0) {
        const hasAllRequired = requireOf.every((field) => (
            hasManualValidationFieldValue(getManualValidationFieldValue(blockState, field))
        ));
        if (!hasAllRequired) {
            return rule.message || `Please complete required fields before setting status to ${rule.status}.`;
        }
        return null;
    }

    if (requireAnyOf.length > 0) {
        const hasAnyRequired = requireAnyOf.some((field) => (
            hasManualValidationFieldValue(getManualValidationFieldValue(blockState, field))
        ));
        if (!hasAnyRequired) {
            return rule.message || `Please complete required fields before setting status to ${rule.status}.`;
        }
    }

    return null;
};

/**
 * Returns the new status to set when a field (key) is changed.
 * @param {string} key - The field that was changed
 * @param {*} value - The new value (truthy = entered, falsy = cleared)
 * @param {Object} blockState - Current block state (for whenValueCleared logic)
 * @param {Array} blocks - Blocks to get statusTransition config from
 * @returns {{ statusField: string, newStatus: string } | { statusField: string, clear: true } | null}
 */
export const getStatusTransitionForFieldChange = (key, value, blockState, blocks) => {
    if (!blocks?.length) return null;
    const config = getStatusTransitionFromBlocks(blocks);
    const statusField = config.statusField;
    if (!statusField) return null;

    const hasValue = value != null && value !== '';

    // When tier control field is cleared, always revert status to Deal Review (even if block config omits topTierRequired)
    const controlField = getTierControlFieldFromBlocks(blocks);
    if (key === controlField && !hasValue) {
        return { statusField, newStatus: 'Deal Review' };
    }

    const transitions = config.transitions || [];

    for (const t of transitions) {
        const triggerFields = Array.isArray(t.triggerFields) ? t.triggerFields : [];
        if (!triggerFields.includes(key)) continue;

        // Tier-aware: for tier approval date fields, only trigger when the changed field's tier
        // matches the selected top tier. E.g. if topTierRequired = "Tier 1", only tier1ApprovalDate triggers.
        const keyTier = extractTierFromFieldName(key);
        const hasTierApprovalFields = triggerFields.some((f) => extractTierFromFieldName(f) != null);
        if (hasTierApprovalFields && keyTier != null) {
            const topTierVal = blockState[controlField];
            const maxTier = parseMaxTierFromTopTierValue(topTierVal);
            if (maxTier == null || keyTier !== maxTier) continue;
        }

        if (hasValue) {
            if (t.onlyWhenCleared) return null;
            return { statusField, newStatus: t.toStatus || '' };
        }
        return { statusField, newStatus: t.whenValueCleared ?? null, clear: !t.whenValueCleared };
    }
    return null;
};

/**
 * Returns validation error when a status value violates manualStatusValidation rules.
 */
export const getManualStatusValidationErrorForStatus = (statusValue, blockState, blocks) => {
    const config = getStatusTransitionFromBlocks(blocks);
    const rules = config?.manualStatusValidation;
    if (!Array.isArray(rules) || rules.length === 0) {
        return null;
    }

    const normalizedStatus = normalizeManualValidationFieldValue(statusValue);
    if (!normalizedStatus) {
        return null;
    }

    for (const rule of rules) {
        const error = getManualStatusValidationErrorForRule(rule, normalizedStatus, blockState);
        if (error) {
            return error;
        }
    }
    return null;
};

/**
 * Returns validation error when user manually changes the status field.
 */
export const getManualStatusValidationErrorForChange = (fieldKey, newStatusValue, blockState, blocks) => {
    const config = getStatusTransitionFromBlocks(blocks);
    const statusField = config?.statusField || 'negotiationStatus';
    if (fieldKey !== statusField) {
        return null;
    }

    const mergedState = {
        ...blockState,
        [statusField]: newStatusValue,
    };
    return getManualStatusValidationErrorForStatus(newStatusValue, mergedState, blocks);
};

/**
 * Returns manual status validation errors for save.
 */
export const getManualStatusValidationErrors = (blockState, blocks) => {
    const config = getStatusTransitionFromBlocks(blocks);
    const rules = config?.manualStatusValidation;
    const errors = [];

    if (!Array.isArray(rules) || rules.length === 0) {
        return { isValid: true, errors };
    }

    const statusField = config.statusField || 'negotiationStatus';
    const currentStatus = getManualValidationFieldValue(blockState, statusField);
    const error = getManualStatusValidationErrorForStatus(currentStatus, blockState, blocks);
    if (error) {
        errors.push({ label: error });
    }

    return { isValid: errors.length === 0, errors };
};

export const DEFAULT_TIER_DATE_RULES = [
    { beforeField: 'tier0TargetReviewDate', afterField: 'tier1TargetReviewDate', message: 'Tier 1 target date must be after Tier 0 target date' },
    { beforeField: 'tier1TargetReviewDate', afterField: 'tier2TargetReviewDate', message: 'Tier 2 target date must be after Tier 1 target date' },
    { beforeField: 'tier0TargetReviewDate', afterField: 'tier2TargetReviewDate', message: 'Tier 2 target date must be after Tier 0 target date' },
    { beforeField: 'tier0RequestedReviewDate', afterField: 'tier1RequestedReviewDate', message: 'Tier 1 scheduled date must be after Tier 0 scheduled date' },
    { beforeField: 'tier1RequestedReviewDate', afterField: 'tier2RequestedReviewDate', message: 'Tier 2 scheduled date must be after Tier 1 scheduled date' },
    { beforeField: 'tier0RequestedReviewDate', afterField: 'tier2RequestedReviewDate', message: 'Tier 2 scheduled date must be after Tier 0 scheduled date' },
];

/** Default tier date validation mode: inline warning (does not block save). */
export const DEFAULT_TIER_DATE_VALIDATION_MODE = 'warning';

/**
 * Resolves tier date validation config from blocks.
 * @returns {{ mode: 'warning' | 'error', rules: Array }}
 */
export const getTierDateValidationConfigFromBlocks = (blocks) => {
    let mode = DEFAULT_TIER_DATE_VALIDATION_MODE;
    const rules = [];
    const collect = (block) => {
        const cfg = block?.config?.tierDateValidation;
        if (!cfg) return;
        if (cfg.mode === 'error' || cfg.mode === 'warning') {
            mode = cfg.mode;
        }
        if (Array.isArray(cfg.rules) && cfg.rules.length > 0) {
            rules.push(...cfg.rules);
        }
    };
    if (blocks?.length) {
        blocks.forEach((b) => {
            if (b.subBlock?.length) b.subBlock.forEach(collect);
            else collect(b);
        });
    }
    return {
        mode,
        rules: rules.length > 0 ? rules : DEFAULT_TIER_DATE_RULES,
    };
};

/**
 * Returns tier date error message for a specific field, given rules.
 * @param {Object} blockState
 * @param {string} elementValue - field value (e.g. 'tier1TargetReviewDate')
 * @param {Array} rules - [{ beforeField, afterField, message }]
 * @returns {string|null}
 */
export const getTierDateErrorForElement = (blockState, elementValue, rules = DEFAULT_TIER_DATE_RULES) => {
    if (!blockState || !elementValue || !rules?.length) return null;
    const parseDate = (val) => {
        if (val == null || val === '') return null;
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d;
    };
    const getVal = (field) => parseDate(getValueFromPath(blockState, field) ?? blockState[field]);
    for (const rule of rules) {
        const isRelevantField = rule.afterField === elementValue || rule.beforeField === elementValue;
        if (!isRelevantField) continue;
        const beforeVal = getVal(rule.beforeField);
        const afterVal = getVal(rule.afterField);
        if (beforeVal != null && afterVal != null && beforeVal > afterVal) return rule.message;
    }
    return null;
};

/**
 * Returns tier date validation errors (config-based or default).
 * @param {Object} blockState
 * @param {Array} blocks - optional; used to get block.config.tierDateValidation.rules
 * @returns {{ isValid: boolean, errors: Array<{ label: string }>, mode: 'warning' | 'error' }}
 */
export const getTierDateValidationErrors = (blockState, blocks) => {
    const errors = [];
    const { mode, rules } = getTierDateValidationConfigFromBlocks(blocks);
    if (!blockState) return { isValid: true, errors, mode };
    const parseDate = (val) => {
        if (val == null || val === '') return null;
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d;
    };
    const getVal = (field) => parseDate(getValueFromPath(blockState, field) ?? blockState[field]);
    for (const rule of rules) {
        const beforeVal = getVal(rule.beforeField);
        const afterVal = getVal(rule.afterField);
        if (beforeVal != null && afterVal != null && beforeVal > afterVal) {
            errors.push({ label: rule.message });
        }
    }
    return { isValid: errors.length === 0, errors, mode };
};

export const getAllDataElementsFromBlocks = (blocks) => {
    if (!blocks?.length) return [];
    const collected = [];
    blocks.forEach((block) => {
        if (block.subBlock?.length) {
            block.subBlock.forEach((res) => {
                const dataElements = res?.config?.dataElements;
                if (dataElements?.length) {
                    collected.push(...(Array.isArray(dataElements) ? dataElements : [dataElements]));
                }
            });
        } else {
            const dataElements = block?.config?.dataElements;
            if (dataElements?.length) {
                collected.push(...(Array.isArray(dataElements) ? dataElements : [dataElements]));
            }
        }
    });
    return collected;
};

/**
 * Returns the control field name (e.g. topTierRequired) from blocks.
 */
export const getTierControlFieldFromBlocks = (blocks) => {
    const collected = getAllDataElementsFromBlocks(blocks);
    const el = collected.find((e) => e.tierControlField === true);
    return el?.value ?? el?.name ?? 'topTierRequired';
};

const parseMaxTierFromTopTierValue = (val) => {
    if (val == null || val === '') return null;
    const m = String(val).match(/tier\s*(\d+)/i);
    return m ? parseInt(m[1], 10) : null;
};

const extractTierFromFieldName = (fieldName) => {
    if (!fieldName) return null;
    const m = String(fieldName).match(/tier(\d+)/i);
    return m ? parseInt(m[1], 10) : null;
};

/**
 * When topTier/control field changes, returns field names to clear.
 * - If topTier cleared (no maxTier): clear all tiered fields
 * - If topTier set (e.g. Tier 1): clear fields with tierLevel > maxTier (e.g. tier 2 fields)
 */
export const getTierFieldsToClearOnTopTierChange = (newTopTierValue, allDataElements) => {
    if (!allDataElements?.length) return [];
    const maxTier = parseMaxTierFromTopTierValue(newTopTierValue);
    const tieredElements = allDataElements.filter((el) => {
        const tier = el.tierLevel ?? extractTierFromFieldName(el.value || el.name);
        return tier != null;
    });
    if (maxTier == null) {
        return tieredElements.map((el) => el.value || el.name).filter(Boolean);
    }
    return tieredElements
        .filter((el) => {
            const tier = el.tierLevel ?? extractTierFromFieldName(el.value || el.name);
            return tier > maxTier;
        })
        .map((el) => el.value || el.name)
        .filter(Boolean);
};
export function conditionMatches(cond, state) {
    const fieldValue = state[cond.selectedConditionDataElement];
    if (fieldValue === undefined || fieldValue === null) return false;
    const op = cond.selectedConditionOperator;
    const target = cond.value;
    if (op === 'equals' || op === 'is_equals') return fieldValue === target;
    if (op === 'is_not_equals') return fieldValue !== target;
    const numVal = Number(fieldValue);
    const numTarget = Array.isArray(target) ? target.map(Number) : Number(target);
    if (op === 'greaterthan') return !Number.isNaN(numVal) && !Number.isNaN(numTarget) && numVal > numTarget;
    if (op === 'greaterthanorequal') return !Number.isNaN(numVal) && !Number.isNaN(numTarget) && numVal >= numTarget;
    if (op === 'lessthan') return !Number.isNaN(numVal) && !Number.isNaN(numTarget) && numVal < numTarget;
    if (op === 'lessthanorequal') return !Number.isNaN(numVal) && !Number.isNaN(numTarget) && numVal <= numTarget;
    // if (op === 'between' && Array.isArray(target) && target.length >= 2) {
    //     const [min, max] = [numTarget[0], numTarget[1]];
    //     return !Number.isNaN(numVal) && !Number.isNaN(min) && !Number.isNaN(max) && numVal >= min && numVal <= max;
    // }
    if (op === 'between') {
        const subCond = cond.subCondition;
        if (Array.isArray(subCond) && subCond.length === 2) {
            return conditionMatches(subCond[0], state) && conditionMatches(subCond[1], state);
        }
        if (Array.isArray(target) && target.length >= 2) {
            const [min, max] = [numTarget[0], numTarget[1]];
            return !Number.isNaN(numVal) && !Number.isNaN(min) && !Number.isNaN(max) && numVal >= min && numVal <= max;
        }
    }
    return false;
}

/**
 * Returns the first condition in element.condition that matches state; only that one is used for calculatedFields.
 */
export function getMatchingCondition(conditionArray, state) {
    if (!conditionArray?.length) return null;
    return conditionArray.find(cond => conditionMatches(cond, state)) || null;
}

function safeEvaluateNumericExpression(exprString) {
    const trimmed = String(exprString).trim();
    if (!trimmed) return NaN;
    if (!/^[\d.\+\-\*\/\(\)\s]+$/.test(trimmed)) return NaN;
    try {
        const fn = new Function('return (' + trimmed + ')');
        const result = fn();
        return typeof result === 'number' ? result : NaN;
    } catch {
        return NaN;
    }
}

/**
 * Compute one calculated field value.
 * Supports:
 * - { type: 'date', operation: 'subtract', operationField, days }
 * - { type: 'string', operation: 'compare', value } (sets fixed string e.g. "Critical", "High", "Medium")
 */
export function computeCalculatedFieldValue(state, config) {
    if (!config) return null;
    if (config.type === 'string' && config.operation === 'compare') {
        return config.value != null ? config.value : null;
    }
    if (config.type === 'date' && config.operation === 'subtract') {
        const sourceVal = state[config.operationField];
        if (sourceVal == null || sourceVal === '') return null;
        
        const daysToSubtract = typeof config.days === 'number' ? config.days : 0;
        if (daysToSubtract < 0 || !Number.isFinite(daysToSubtract)) return null;
        
        let d;
        if (typeof sourceVal === 'string') {
            const trimmed = sourceVal.trim();
            if (!trimmed) return null;
            if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
                d = new Date(trimmed + 'T00:00:00.000Z');
            } else {
                d = new Date(trimmed);
            }
        } else if (sourceVal instanceof Date) {
            d = new Date(sourceVal);
        } else {
            d = new Date(sourceVal);
        }
        
        if (isNaN(d.getTime())) return null;
        
        const result = new Date(d);
        result.setUTCDate(result.getUTCDate() - daysToSubtract);
        
        if (isNaN(result.getTime())) return null;
        
        const year = result.getUTCFullYear();
        const month = String(result.getUTCMonth() + 1).padStart(2, '0');
        const day = String(result.getUTCDate()).padStart(2, '0');
        
        if (year < 1000 || year > 9999) return null;
        
        return `${year}-${month}-${day}`;
    }
    if (config.type === 'number' && config.operation === 'expression') {
        const expression = config.expression;
        if (expression == null || typeof expression !== 'string') return null;
        const fieldNames = Array.isArray(config.fields) ? config.fields : [];
        let safeExpr = expression.trim();
        for (const fieldName of fieldNames) {
            if (typeof fieldName !== 'string') continue;
            const val = state[fieldName];
            const num = val != null && val !== '' ? Number(val) : NaN;
            const replacement = Number.isNaN(num) ? 'NaN' : String(num);
            safeExpr = safeExpr.replace(new RegExp('\\b' + fieldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'g'), replacement);
        }
        const result = safeEvaluateNumericExpression(safeExpr);
        if (result == null || Number.isNaN(result)) return null;
        const decimals = config.decimals;
        const finalValue = typeof decimals === 'number' && decimals >= 0
            ? Number(result.toFixed(decimals))
            : result;
        return finalValue;
    }
    return null;
}

export {
    serializeDealState,
    getChangedFieldsVsPersisted,
    omitDocumentIdentityFields,
} from './dealSnapshot';